import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { SimpleDocumentList } from 'features/Driver/DocumentList/SimpleDocumentList';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { LoaderArrow } from 'components/Loaders/LoaderArrow';
import documentRequestCandidateService from 'store/document-request-candidate/service';
import {
  ScanPage,
  ScanPageWizardStep2,
  ThatSItPage,
  DriverCheckListPagePath,
  OneMoreThingPagePath,
} from 'routes/paths';

import styles from './styles.module.scss';
import { IDriverDocument } from '../../../common/document/interfaces/driver-document';
import { getCameraPermission } from 'functions/browserPermissions';
import { useEffect, useMemo, useState } from 'react';
import { DocumentStatusEnum } from 'common/document/enums/document-status.enum';
import { useQueryParams } from 'hooks/useSearchParams';
import { DriverHeader } from 'App/components/Layouts/Driver/DriverHeader';
import SelectDocumentType from 'features/Driver/SelectDocumentType';
import { localStorageWrapper } from 'functions/localStorage';
import { CreateDocumentRequestQuery } from 'store/document-request-candidate/interfaces';
import { ContentBox } from '../../../components/ContentBox';
import { useGetCandidateDocumentList } from '../../../features/Driver/LicenseCameraScan/useGetCandidateDocumentList';
import ButtonWithArrowIcon from '../../../components/Buttons/ButtonWithArrowIcon';

const validDocumentStatusToGo = [
  DocumentStatusEnum.REJECTED,
  DocumentStatusEnum.REQUESTED,
];

const defaultDocumentDriverLicense: IDriverDocument = {
  id: DocumentTypeEnum.DRIVER_LICENSE,
  candidateId: '',
  documentRequestId: '',
  documentType: DocumentTypeEnum.DRIVER_LICENSE,
  documentName: 'License Front & Back',
  status: DocumentStatusEnum.REQUESTED,
  isDefault: true,
};

const defaultDocumentDotMedical: IDriverDocument = {
  id: DocumentTypeEnum.DOT_MEDICAL,
  candidateId: '',
  documentRequestId: '',
  documentType: DocumentTypeEnum.DOT_MEDICAL,
  documentName: 'Medical Certificate',
  status: DocumentStatusEnum.REQUESTED,
  isDefault: true,
};

const defaultDriverChecklist: IDriverDocument = {
  id: DocumentTypeEnum.DRIVER_CHECKLIST,
  candidateId: '',
  documentRequestId: '',
  documentType: DocumentTypeEnum.DRIVER_CHECKLIST,
  documentName: 'Driver Checklist',
  status: DocumentStatusEnum.REQUESTED,
  isDefault: true,
};

const defaultMedicationReleaseForm: IDriverDocument = {
  id: DocumentTypeEnum.MEDICAL_DISCONTINUANCE,
  candidateId: '',
  documentRequestId: '',
  documentType: DocumentTypeEnum.MEDICAL_DISCONTINUANCE,
  documentName: 'Medical Discontinuance Form',
  status: DocumentStatusEnum.REQUESTED,
  isDefault: true,
};

const defaultReturnToWork: IDriverDocument = {
  id: DocumentTypeEnum.RETURN_TO_WORK,
  candidateId: '',
  documentRequestId: '',
  documentType: DocumentTypeEnum.RETURN_TO_WORK,
  documentName: 'Return to Work',
  status: DocumentStatusEnum.REQUESTED,
  isDefault: true,
};

const documentsToFilterFromList = [DocumentTypeEnum.DRIVER_CHECKLIST];
const documentSort = [
  DocumentTypeEnum.DRIVER_LICENSE,
  DocumentTypeEnum.DOT_MEDICAL,
  DocumentTypeEnum.MEDICAL_DISCONTINUANCE,
  DocumentTypeEnum.RETURN_TO_WORK,
  DocumentTypeEnum.DRIVER_CHECKLIST,
  DocumentTypeEnum.PHOTO_ID,
];

export const DriverDocumentsPage = () => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const isNotVerifiedSession = useMemo(
    () => localStorageWrapper.getSessionType() === 'not-verified',
    []
  );
  const [showDocumentSelect, setShowDocumentSelect] = useState<boolean>(false);

  const { candidateData, isLoading } = useGetCandidateDocumentList();
  const [createDocumentRequest] =
    documentRequestCandidateService.endpoints.createDocumentRequest.useMutation();

  const { allDocuments: documents, documentsForList } = useMemo(() => {
    let finalDocuments = [...(candidateData?.documents || [])];

    if (isNotVerifiedSession) {
      finalDocuments = finalDocuments.filter(
        (d) =>
          d.status &&
          [DocumentStatusEnum.REQUESTED, DocumentStatusEnum.REJECTED].includes(
            d.status
          )
      );
    }

    if (isNotVerifiedSession || showDocumentSelect) {
      if (
        !finalDocuments.some(
          (x) => x.documentType === defaultReturnToWork.documentType
        )
      ) {
        finalDocuments.push(defaultReturnToWork);
      }

      if (
        !finalDocuments.some(
          (x) => x.documentType === defaultMedicationReleaseForm.documentType
        )
      ) {
        finalDocuments.push(defaultMedicationReleaseForm);
      }

      if (
        !finalDocuments.some(
          (x) => x.documentType === defaultDocumentDotMedical.documentType
        )
      ) {
        finalDocuments.push(defaultDocumentDotMedical);
      }

      if (
        !finalDocuments.some(
          (x) => x.documentType === defaultDriverChecklist.documentType
        )
      ) {
        finalDocuments.push(defaultDriverChecklist);
      }

      if (
        !finalDocuments.some(
          (x) => x.documentType === defaultDocumentDriverLicense.documentType
        )
      ) {
        finalDocuments.push(defaultDocumentDriverLicense);
      }
    }

    const listOfDocuments: any[] = [];
    documentSort.forEach((item) => {
      const doc = finalDocuments.find((d) => d.documentType === item);
      doc && listOfDocuments.push(doc);
    });
    return {
      allDocuments: listOfDocuments,
      documentsForList: listOfDocuments,
    };
  }, [candidateData?.documents, showDocumentSelect]);

  useEffect(() => {
    if (queryParams.from && documents && candidateData?.documents) {
      // It forces to go to the checklist after the medical certificate
      if (queryParams.from === DocumentTypeEnum.DOT_MEDICAL) {
        const checklistDocument = documents.find(
          (document) =>
            document.documentType == DocumentTypeEnum.DRIVER_CHECKLIST
        );

        if (
          checklistDocument?.status &&
          ![
            DocumentStatusEnum.NEEDS_REVIEW,
            DocumentStatusEnum.UPLOADED,
          ].includes(checklistDocument.status)
        ) {
          handleOpenDocumentPage(checklistDocument);
          return;
        }
      }

      // It forces to go to the Photo ID after the temp driver license
      if (queryParams.from === DocumentTypeEnum.DRIVER_LICENSE) {
        const photoIdDocument = documents.find(
          (document) => document.documentType == DocumentTypeEnum.PHOTO_ID
        );
        if (
          photoIdDocument?.id !== DocumentTypeEnum.PHOTO_ID &&
          photoIdDocument?.status &&
          [DocumentStatusEnum.REJECTED, DocumentStatusEnum.REQUESTED].includes(
            photoIdDocument.status
          )
        ) {
          handleOpenDocumentPage(photoIdDocument);
          return;
        }
      }

      const documentToGo = documents.find(
        (document) =>
          document.documentType !== queryParams.from &&
          document.status &&
          validDocumentStatusToGo.includes(document.status) &&
          !documentsToFilterFromList.includes(document.documentType) &&
          !document.isDefault
      );

      if (queryParams.showOneMoreThing) {
        navigate(
          `${OneMoreThingPagePath}?${documentToGo ? 'showNext=true' : ''}`
        );
      } else {
        if (!documentToGo) {
          navigate(
            `${ThatSItPage}${isNotVerifiedSession ? '?goDocList=true' : ''}`
          );
        }
      }
    }
  }, [queryParams, documents, candidateData?.documents]);

  const goToScanPage = async (
    documentRequestId: string,
    documentId: string,
    documentType: string,
    documentTypeEnum: DocumentTypeEnum
  ) => {
    const permission = await getCameraPermission();

    const goTo = (permission === 'granted' ? ScanPageWizardStep2 : ScanPage)
      .replace(':documentRequestId', documentRequestId)
      .replace(':documentId', documentId)
      .replace(':documentType', documentType)
      .replace(':documentTypeEnum', documentTypeEnum || '');
    navigate(goTo);
  };

  const handleOpenDocumentPage = async (
    document: IDriverDocument,
    createNewDocument?: boolean
  ) => {
    let documentRequestId = document.documentRequestId;
    let documentId = document.id;

    if (document.isDefault || createNewDocument) {
      const docs: CreateDocumentRequestQuery['response'] = (
        (await createDocumentRequest({
          documentType: document.documentType,
        })) as any
      )?.data;

      if (!docs) {
        return;
      }

      const createdDocument = docs?.find(
        (d) => d.documentType === document.documentType
      );

      documentId = createdDocument?.id || '';
      documentRequestId = createdDocument?.documentRequestId || '';
    }

    switch (document.documentType) {
      case DocumentTypeEnum.DRIVER_LICENSE:
        goToScanPage(
          documentRequestId,
          documentId,
          'license',
          document.documentType
        );
        break;
      case DocumentTypeEnum.DOT_MEDICAL:
        goToScanPage(
          documentRequestId,
          documentId,
          'dot-med',
          document.documentType
        );
        break;
      case DocumentTypeEnum.RETURN_TO_WORK:
        goToScanPage(
          documentRequestId,
          documentId,
          'generic',
          document.documentType
        );
        break;
      case DocumentTypeEnum.MEDICAL_DISCONTINUANCE:
        goToScanPage(
          documentRequestId,
          documentId,
          'generic',
          document.documentType
        );
        break;
      case DocumentTypeEnum.PHOTO_ID:
        goToScanPage(
          documentRequestId,
          documentId,
          'generic',
          document.documentType
        );
        break;
      case DocumentTypeEnum.DRIVER_CHECKLIST:
        navigate(`${DriverCheckListPagePath}/${documentId}`);
        break;
    }
  };

  if (isLoading) {
    return <LoaderArrow />;
  }

  if (isNotVerifiedSession || showDocumentSelect) {
    return (
      <SelectDocumentType
        documentsToSelect={documentsForList}
        onContinue={async (document) => {
          await handleOpenDocumentPage(document, showDocumentSelect);
        }}
        onGoBack={
          isNotVerifiedSession ? undefined : () => setShowDocumentSelect(false)
        }
      />
    );
  }

  return (
    <div>
      <div className={styles.logoContainer}>
        <DriverHeader />
      </div>
      <div className={styles.root}>
        <div>
          <div className={styles.documents}>
            <Typography className={styles.label}>
              Requested Documents
            </Typography>
            {documentsForList?.length ? (
              <SimpleDocumentList
                documentList={documentsForList}
                onAction={handleOpenDocumentPage}
              />
            ) : (
              <ContentBox className={styles.noDocuments}>
                No document request at this time
              </ContentBox>
            )}
          </div>
          <div className={styles.actions}>
            <ButtonWithArrowIcon
              variant="contained"
              color="primary"
              onClick={() => setShowDocumentSelect(true)}
            >
              Upload Document
            </ButtonWithArrowIcon>
          </div>
        </div>
      </div>
    </div>
  );
};
